/* eslint-disable react/prop-types */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import {
  fetchSupportAttachment,
  repairSupportFilename,
} from "../../../services/SupportService";
import "./supportAttachmentsViewer.css";

const getAttachmentKind = (attachment) => {
  const mime = String(attachment?.mimeType || "").toLowerCase();
  const name = String(attachment?.originalName || attachment?.filename || "").toLowerCase();
  if (mime.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp)$/i.test(name)) {
    return "image";
  }
  if (mime === "application/pdf" || name.endsWith(".pdf")) {
    return "pdf";
  }
  return "other";
};

function SupportAttachmentsViewer({
  ticketId,
  attachments = [],
  initialIndex = 0,
  onClose,
}) {
  const [activeIndex, setActiveIndex] = useState(
    Math.min(Math.max(initialIndex, 0), Math.max(attachments.length - 1, 0))
  );
  const [previewUrl, setPreviewUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const isGallery = attachments.length > 1;
  const active = attachments[activeIndex];
  const activeKind = getAttachmentKind(active);
  const activeName = repairSupportFilename(
    active?.originalName || active?.filename || "Attachment"
  );
  const hasPrev = activeIndex > 0;
  const hasNext = activeIndex < attachments.length - 1;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    if (!ticketId || !active?.filename) return undefined;
    let objectUrl = "";
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setPreviewUrl("");

    fetchSupportAttachment(ticketId, active.filename)
      .then(({ blobUrl }) => {
        if (cancelled) {
          URL.revokeObjectURL(blobUrl);
          return;
        }
        objectUrl = blobUrl;
        setPreviewUrl(blobUrl);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setFailed(true);
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [ticketId, active?.filename]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose?.();
      if (!isGallery) return;
      if (event.key === "ArrowLeft" && hasPrev) {
        setActiveIndex((index) => index - 1);
      }
      if (event.key === "ArrowRight" && hasNext) {
        setActiveIndex((index) => index + 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [hasNext, hasPrev, isGallery, onClose]);

  if (!attachments.length) return null;

  return createPortal(
    <div className="sav-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <button type="button" className="sav-close" onClick={onClose} aria-label="Close">
        <i className="fas fa-times" />
      </button>

      {isGallery && (
        <button
          type="button"
          className="sav-nav sav-nav--prev"
          onClick={(event) => {
            event.stopPropagation();
            if (hasPrev) setActiveIndex((index) => index - 1);
          }}
          disabled={!hasPrev}
          aria-label="Previous"
        >
          <i className="fas fa-chevron-left" />
        </button>
      )}

      <div className="sav-stage" onClick={(event) => event.stopPropagation()}>
        {loading && <p className="sav-fallback">Loading...</p>}
        {!loading && failed && (
          <p className="sav-fallback">This file could not be loaded.</p>
        )}
        {!loading && !failed && activeKind === "image" && previewUrl && (
          <img className="sav-image" src={previewUrl} alt={activeName} />
        )}
        {!loading && !failed && activeKind === "pdf" && previewUrl && (
          <iframe className="sav-pdf" src={previewUrl} title={activeName} />
        )}
        {!loading && !failed && activeKind === "other" && (
          <p className="sav-fallback">Preview is not available for this file.</p>
        )}
        <p className="sav-caption">
          {activeName}
          {isGallery ? ` · ${activeIndex + 1} / ${attachments.length}` : ""}
        </p>
      </div>

      {isGallery && (
        <button
          type="button"
          className="sav-nav sav-nav--next"
          onClick={(event) => {
            event.stopPropagation();
            if (hasNext) setActiveIndex((index) => index + 1);
          }}
          disabled={!hasNext}
          aria-label="Next"
        >
          <i className="fas fa-chevron-right" />
        </button>
      )}
    </div>,
    document.body
  );
}

export default SupportAttachmentsViewer;
